import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { CSVLink } from 'react-csv';
import KPIHeader from '../../Header/KPIHeader';
import KPISubHeader from '../../Header/SubHeader/KPISubHeader';
import SchemeDocumentRatioDashboard from '../../KPI/Scheme Diversity/SchemeDocumentRatio';
import { Navigate } from "react-router-dom";
import SchemeDiversityTable from '../../KPI/Scheme Diversity/SchemeDiversityTable';
import SchemeDiversityBRTable from '../../KPI/Scheme Diversity/SchemeDiversityBRTable';
import SchemeDocumentRatioDS from '../../KPI/Scheme Diversity/SchemeDocumentRationDS';
import SchemeDocumentRatioOpen from '../../KPI/Scheme Diversity/SchemeDocumentRationOpen';
import SchemeDiversityDSTable from '../../KPI/Scheme Diversity/DSTable';
import CitizenStatsTable from '../../KPI/Project Level/CitizenStatsTable';
import SchemeDiversityDefitions from '../../Defintions/SchemeDiversityDefitions';
import { getSession } from "../../utils/session";
import { setProjectDetails } from "../../userSlice";

const SchemeDiversity = () => {
  const dispatch = useDispatch();

  const reduxEmail = useSelector((state) => state.user.email);
  const reduxProjectName = useSelector((state) => state.user.projectName);
  const reduxPid = useSelector((state) => state.user.projectId);
  const reduxRoleId = useSelector((state) => state.user.roleId);

  const [email, setEmail] = useState(reduxEmail);
  const [projectName, setProjectName] = useState(reduxProjectName);
  const [pid, setPid] = useState(reduxPid);
  const [roleId, setRoleId] = useState(reduxRoleId);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('BR');
  const [view, setView] = useState("br");

  const [apiData, setApiData] = useState({ 
    scheme_diversity: {}, 
    schemes: [], 
    states: [],
    total_cases: 0 
  });
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statesPerPage, setStatesPerPage] = useState(5);
  const [schemesPerPage, setSchemesPerPage] = useState(5);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [projectStates, setProjectStates] = useState([]);
  const [projectSchemes, setProjectSchemes] = useState([]);
  const [statePage, setStatePage] = useState(0);
  const [schemePage, setSchemePage] = useState(0);
  const tableRef = useRef(null);

  useEffect(() => {
    const session = getSession();
    if (session?.email) {
      setEmail(session.email);
      setProjectName(session.projectName);
      setPid(session.projectId);
      setRoleId(session.roleId);
      dispatch(setProjectDetails({
        email: session.email,
        projectName: session.projectName,
        projectId: session.projectId,
        organizationName: session.organizationName,
        roleId: session.roleId,
      }));
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    if (!pid) return;
    const fetchOptions = async () => {
      try {
        const [statesRes, schemesRes] = await Promise.all([
          fetch(`https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/unique-state/?pid=${pid}`),
          fetch(`https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/unique-scheme/?pid=${pid}`)
        ]);
        const statesData = await statesRes.json();
        const schemesData = await schemesRes.json();
        setProjectStates(statesData.states.map(s => ({ value: s, label: s })));
        setProjectSchemes(schemesData.schemes.map(s => ({ value: s, label: s })));
      } catch (err) {
        console.error("Error fetching options:", err);
      }
    };
    fetchOptions();
  }, [pid]);

  const fetchData = async () => {
    if (!pid) return;
    setTableLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('pid', pid);
      selectedStates.forEach(state => params.append('State', state.value));
      selectedSchemes.forEach(scheme => params.append('SchemeName', scheme.value));
      const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/?${params.toString()}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setApiData(data);
      setStatePage(0);
      setSchemePage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (pid) fetchData();
  }, [pid]);

  const applyFilters = () => fetchData();
  const clearFilters = () => {
    setSelectedStates([]);
    setSelectedSchemes([]);
    fetchData();
  };

  const prepareCSVData = () => {
    const { scheme_diversity, schemes, states } = apiData;
    const csvData = [];
    // ✅ Added SchemeValue after Scheme Name in CSV headers
    const headers = ['Scheme', 'Scheme Value', ...states, '% of Total', 'Total Cases', 'BR Cases', 'Benefit Value'];
    csvData.push(headers);
    schemes.forEach(scheme => {
      const row = [
        scheme,
        scheme_diversity[scheme]?.SchemeValue ?? '',   // ✅ NEW
        ...states.map(state => scheme_diversity[scheme]?.[state] || '0%'),
        scheme_diversity[scheme]?.['% of Total'] || '0%',
        scheme_diversity[scheme]?.total_cases || 0,
        scheme_diversity[scheme]?.['br_count'] || 0,
        scheme_diversity[scheme]?.['total_scheme_value'] || 0
      ];
      csvData.push(row);
    });
    if (scheme_diversity.column_total) {
      const totalRow = [
        'Total',
        '',                                             // ✅ Empty for SchemeValue in total row
        ...states.map(state => scheme_diversity.column_total[state] || '0%'),
        scheme_diversity.column_total['% of Total'] || '100%',
        apiData.total_cases || 0,
        scheme_diversity.column_total['br_count'] || 0,
        scheme_diversity.column_total['total_scheme_value'] || 0
      ];
      csvData.push(totalRow);
    }
    return csvData;
  };

  const paginatedStates = apiData.states?.slice(statePage * statesPerPage, (statePage + 1) * statesPerPage) || [];
  const paginatedSchemes = apiData.schemes?.slice(schemePage * schemesPerPage, (schemePage + 1) * schemesPerPage) || [];
  const totalStatePages = Math.ceil(apiData.states?.length / statesPerPage) || 1;
  const totalSchemePages = Math.ceil(apiData.schemes?.length / schemesPerPage) || 1;

  if (loading) return null;
  if (!email) return <Navigate to="/" replace />;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="bg-gray-50 min-h-screen">
      <KPIHeader />
      <div className="mt-24">
        <KPISubHeader />

        {/* Project Title */}
        <div className="px-6 py-2 flex items-center justify-between border-b border-gray-200 mb-3 bg-white rounded-lg shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 truncate max-w-xs md:max-w-md">{projectName}</h1>
          </div>
          <span><SchemeDiversityDefitions /></span>
          <span className="bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 text-sm font-semibold px-4 py-2 rounded-full shadow-sm flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            PID: {pid}
          </span>
        </div>

        {/* Diversity in Schemes Table */}
        <div className="border max-w-[92vw] border-gray-200 rounded-md ml-[3vw] p-4 my-4 shadow-md shadow-black">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-pink-600">Diversity in Schemes</h3>
          </div>

          {/* Filters */}
          <div className="p-4 bg-white shadow rounded-lg mb-4 max-w-[90vw]">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">States</label>
                <Select isMulti options={projectStates} value={selectedStates} onChange={setSelectedStates} placeholder="All States" className="react-select-container" classNamePrefix="react-select" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Schemes</label>
                <Select isMulti options={projectSchemes} value={selectedSchemes} onChange={setSelectedSchemes} placeholder="All Schemes" className="react-select-container" classNamePrefix="react-select" />
              </div>
              <div className="flex col-span-2 items-end space-x-2">
                <button onClick={applyFilters} disabled={tableLoading} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50">
                  {tableLoading ? 'Loading...' : 'Apply'}
                </button>
                <button onClick={clearFilters} disabled={tableLoading} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition disabled:opacity-50">Clear</button>
                <CSVLink data={prepareCSVData()} filename="scheme-diversity.csv" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition">
                  Export CSV
                </CSVLink>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="space-y-4">
              {/* Pagination Controls */}
              <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <label className="text-lg font-medium text-gray-700">States per page:</label>
                  <select value={statesPerPage} onChange={(e) => setStatesPerPage(Number(e.target.value))} className="block w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                    {[1, 2, 3, 5, 10, 20].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-lg font-medium text-gray-700">Schemes per page:</label>
                  <select value={schemesPerPage} onChange={(e) => setSchemesPerPage(Number(e.target.value))} className="block w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {apiData.states?.length > statesPerPage && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-blue-800">
                    Showing {statePage * statesPerPage + 1}–{Math.min((statePage + 1) * statesPerPage, apiData.states.length)} of {apiData.states.length} states
                  </span>
                  <div className="flex space-x-2">
                    <button onClick={() => setStatePage(p => Math.max(0, p - 1))} disabled={statePage === 0} className="px-3 py-1 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50">Previous</button>
                    <span className="px-3 py-1 text-sm font-medium">Page {statePage + 1} of {totalStatePages}</span>
                    <button onClick={() => setStatePage(p => Math.min(totalStatePages - 1, p + 1))} disabled={statePage >= totalStatePages - 1} className="px-3 py-1 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 disabled:opacity-50">Next</button>
                  </div>
                </div>
              )}

              {apiData.schemes?.length > schemesPerPage && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-green-800">
                    Showing {schemePage * schemesPerPage + 1}–{Math.min((schemePage + 1) * schemesPerPage, apiData.schemes.length)} of {apiData.schemes.length} schemes
                  </span>
                  <div className="flex space-x-2">
                    <button onClick={() => setSchemePage(p => Math.max(0, p - 1))} disabled={schemePage === 0} className="px-3 py-1 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50 disabled:opacity-50">Previous</button>
                    <span className="px-3 py-1 text-sm font-medium">Page {schemePage + 1} of {totalSchemePages}</span>
                    <button onClick={() => setSchemePage(p => Math.min(totalSchemePages - 1, p + 1))} disabled={schemePage >= totalSchemePages - 1} className="px-3 py-1 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50 disabled:opacity-50">Next</button>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="bg-white max-w-[90vw] rounded-lg shadow-lg mt-4">
              {tableLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-lg font-medium text-gray-700">Loading Table Data...</p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto" ref={tableRef}>
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                    <thead className="bg-gray-100 text-black">
                      <tr>
                        <th className="py-2  text-center text-sm font-semibold uppercase border border-gray-300">Scheme Name</th>
                        {/* ✅ NEW: SchemeValue column right after Scheme Name */}
                        <th className="py-2  text-center text-sm font-semibold uppercase border border-gray-300">Benefit Value</th>
                        {paginatedStates.map(state => (
                          <th key={state} className="py-2 text-center text-sm font-semibold uppercase border border-gray-300">{state}</th>
                        ))}
                        <th className="py-2  text-center text-sm font-semibold uppercase border border-gray-300">% Total</th>
                        <th className="py-2  text-center text-sm font-semibold uppercase border border-gray-300">Total Cases Created</th>
                        <th className="py-2  text-center text-sm font-semibold uppercase border border-gray-300">BR Cases</th>
                        <th className="py-2 text-center text-sm font-semibold uppercase border border-gray-300">Total Benefit Value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedSchemes.map(scheme => (
                        <tr key={scheme} className="hover:bg-blue-50 transition-colors duration-150">
                          <td className="py-2 text-left font-medium border border-gray-200 whitespace-nowrap text-gray-800">{scheme}</td>
                          {/* ✅ NEW: SchemeValue cell */}
                          <td className="py-2 px-2 text-center border border-gray-200 whitespace-nowrap text-gray-700">
                            {apiData.scheme_diversity[scheme]?.SchemeValue ?? '—'}
                          </td>
                          {paginatedStates.map(state => (
                            <td key={`${scheme}-${state}`} className="py-2 px-2 text-center border border-gray-200 whitespace-nowrap text-gray-700">
                              {apiData.scheme_diversity[scheme]?.[state] || '0%'}
                            </td>
                          ))}
                          <td className="py-2 px-2 text-center border border-gray-200 whitespace-nowrap text-gray-700">{apiData.scheme_diversity[scheme]?.['% of Total']}</td>
                          <td className="py-2 px-2 text-center border border-gray-200 whitespace-nowrap text-gray-700">{apiData.scheme_diversity[scheme]?.total_cases || 0}</td>
                          <td className="py-2 px-2 text-center border border-gray-200 whitespace-nowrap text-gray-700">{apiData.scheme_diversity[scheme]?.['br_count']}</td>
                          <td className="py-2  text-center border border-gray-200 whitespace-nowrap text-gray-700">{apiData.scheme_diversity[scheme]?.['total_scheme_value']}</td>
                        </tr>
                      ))}

                      {/* Total Row */}
                      {apiData.scheme_diversity?.column_total && (
                        <tr className="bg-blue-100 font-semibold">
                          <td className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">Total</td>
                          {/* ✅ Empty for SchemeValue in total row */}
                          <td className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">—</td>
                          {paginatedStates.map(state => (
                            <td key={`total-${state}`} className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">
                              {apiData.scheme_diversity.column_total[state] || '0%'}
                            </td>
                          ))}
                          <td className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">{apiData.scheme_diversity.column_total['% of Total']}</td>
                          <td className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">{apiData.total_cases || 0}</td>
                          <td className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">{apiData.scheme_diversity.column_total['br_count']}</td>
                          <td className="py-2 px-2 text-center border border-gray-300 whitespace-nowrap text-gray-800">{apiData.scheme_diversity.column_total['total_scheme_value']}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scheme VS Document Ratio */}
        <div className="border max-w-[92vw] border-gray-200 rounded-md ml-[3vw] p-4 my-4 shadow-md shadow-black">
          <div className="flex mb-4">
            <h3 className="text-xl font-bold text-pink-600">Scheme VS Document Ratio</h3>
            <div className="flex space-x-3 ml-[20vw]">
              <button onClick={() => setActiveTab('BR')} className={`px-4 py-2 rounded-md ${activeTab === 'BR' ? 'text-pink-800 bg-pink-300' : 'bg-gray-100  hover:bg-gray-200'}`}>BR</button>
              <button onClick={() => setActiveTab('DS')} className={`px-4 py-2 rounded-md ${activeTab === 'DS' ? 'text-pink-800 bg-pink-300' : 'bg-gray-100  hover:bg-gray-200'}`}>DS</button>
              <button onClick={() => setActiveTab('Open')} className={`px-4 py-2 rounded-md ${activeTab === 'Open' ? 'text-pink-800 bg-pink-300' : 'bg-gray-100  hover:bg-gray-200'}`}>Open</button>
            </div>
          </div>
          <div>
            {activeTab === 'BR' && <SchemeDocumentRatioDashboard />}
            {activeTab === 'DS' && <SchemeDocumentRatioDS />}
            {activeTab === 'Open' && <SchemeDocumentRatioOpen />}
          </div>
        </div>

        {/* Scheme Ratio Overview */}
        {roleId !== 28 && (
          <div className="border max-w-[92vw] border-gray-200 rounded-md ml-[3vw] p-4 my-4 shadow-md shadow-black">
            <div className="flex mb-4">
              <h3 className="text-xl font-bold text-pink-600">Scheme Ratio Overview</h3>
              <div className="flex space-x-3 ml-[20vw]">
                <button onClick={() => setView("br")} className={`px-4 py-2 rounded-md text-sm font-medium transition ${view === "br" ? 'text-pink-800 bg-pink-300' : "bg-gray-100  text-gray-800 hover:bg-gray-200"}`}>BR</button>
                <button onClick={() => setView("ds")} className={`px-4 py-2 rounded-md text-sm font-medium transition ${view === "ds" ? "text-pink-800 bg-pink-300" : "bg-gray-100  text-gray-800 hover:bg-gray-200"}`}>DS</button>
                <button onClick={() => setView("open")} className={`px-4 py-2 rounded-md text-sm font-medium transition ${view === "open" ? "text-pink-800 bg-pink-300" : "bg-gray-100  text-gray-800 hover:bg-gray-200"}`}>Open</button>
              </div>
            </div>
            {view === "br" && <SchemeDiversityBRTable />}
            {view === "ds" && <SchemeDiversityDSTable />}
            {view === "open" && <SchemeDiversityTable />}
          </div>
        )}

        <div>
          <CitizenStatsTable />
        </div>
      </div>
    </div>
  );
};

export default SchemeDiversity;